import { IStairConfig, IStepGeometry } from '@stair-craft/common/util';

import {
    IPlanPoint,
    clipByConvex,
    isConvexPlan,
    planAdd,
    planArea,
    planConvexHull,
    planDot,
    planNormalToward,
    planPerimeter,
    planSub,
} from '../shared-plan-geometry/plan-geometry';
import { treadFootprint } from '../shared-tread-footprint/tread-footprint';

/** Headroom of the whole stair and of every tread that has something above it (mm). */
export interface IHeadroomResult {
    /** The lowest clearance over the stair; `floorHeight` when nothing hangs over any tread. */
    readonly headroom: number;
    /** Clearance over each tread that lies under the slab or under a higher part of the stair, by step index. */
    readonly stepHeadroom: ReadonlyMap<number, number>;
}

/** An overlap thinner than this is two outlines touching along an edge, not one under the other (mm). */
const MIN_OVERLAP: number = 0.5;

/** The nosing line over one tread: its height rises from the tread's nose tip to the next one. */
interface INosingFrame {
    readonly top: number;
    readonly rise: number;
    readonly riserPoint: IPlanPoint;
    readonly forward: IPlanPoint;
    readonly going: number;
    readonly nose: number;
}

/** A tread in room millimetres: its walking outline and the nosing line over it. */
interface ITreadPlan {
    readonly index: number;
    readonly outline: IPlanPoint[];
    readonly nosing: INosingFrame;
}

/** Something a tread passes under: its outline in plan and the height of its underside. */
interface IOverhead {
    readonly outline: IPlanPoint[];
    readonly underside: number;
    /** The top face; the slab's is past every tread it can hang over. */
    readonly top: number;
    /** How thick the overlap in plan must be to count as overhead (mm). */
    readonly minOverlap: number;
}

function toRoom(points: readonly IPlanPoint[], placement: IPlanPoint): IPlanPoint[] {
    return points.map((point: IPlanPoint) => planAdd(point, placement));
}

/**
 * The riser line, walking direction and going of a tread. A flight tread or landing walks along
 * its own +Z turned by `rotation`; a winder walks from its leading edge toward the middle of its
 * trailing one.
 */
function nosingFrame(step: IStepGeometry, rise: number, placement: IPlanPoint): INosingFrame {
    const corners: IStepGeometry['winderCorners'] = step.winderCorners;
    if (step.isWinder && corners) {
        const trailMid: IPlanPoint = {
            x: (corners.innerFront.x + corners.outerFront.x) / 2,
            z: (corners.innerFront.z + corners.outerFront.z) / 2,
        };
        const forward: IPlanPoint = planNormalToward(corners.innerBack, corners.outerBack, trailMid);
        const going: number = Math.max(1, planDot(planSub(trailMid, corners.innerBack), forward));
        return { forward, going, rise, top: step.position.y, nose: step.noseLength, riserPoint: planAdd(corners.innerBack, placement) };
    }
    const angle: number = (step.rotation * Math.PI) / 180;
    // Same turn as Object3D.rotateY: the local +Z goes to (sin, cos).
    const forward: IPlanPoint = { x: Math.sin(angle), z: Math.cos(angle) };
    const centre: IPlanPoint = { x: step.position.x + step.treadWidth / 2, z: step.position.z + (step.treadDepth - step.noseLength) / 2 };
    const riserOffset: number = -(step.treadDepth - step.noseLength) / 2;
    return {
        forward,
        rise,
        top: step.position.y,
        going: step.treadDepth,
        nose: step.noseLength,
        riserPoint: planAdd(planAdd(centre, forward, riserOffset), placement),
    };
}

/** Height of the nosing line over a point of the tread: the top at the nose tip, the next top at the next nose tip. */
function nosingHeight(frame: INosingFrame, point: IPlanPoint): number {
    const along: number = (planDot(planSub(point, frame.riserPoint), frame.forward) + frame.nose) / frame.going;
    return frame.top + frame.rise * Math.min(1, Math.max(0, along));
}

/** The part of `subject` under `overhead`; either may be concave, a concave pair goes by the hull. */
function overlap(subject: readonly IPlanPoint[], overhead: readonly IPlanPoint[]): IPlanPoint[] {
    if (isConvexPlan(overhead)) {
        return clipByConvex(subject, overhead);
    }
    if (isConvexPlan(subject)) {
        return clipByConvex(overhead, subject);
    }
    return clipByConvex(subject, planConvexHull(overhead));
}

/** Clearance from the nosing line of a tread up to one overhead underside, or null when it is not over the tread. */
function clearanceUnder(tread: ITreadPlan, overhead: IOverhead): number | null {
    const shared: IPlanPoint[] = overlap(tread.outline, overhead.outline);
    if (shared.length < 3) {
        return null;
    }
    const perimeter: number = planPerimeter(shared);
    // Area over half the perimeter: the width of a thin strip, the size of a compact piece. A clip
    // that collapsed to one point has no perimeter, and 0 / 0 is NaN, which no comparison rejects:
    // a tread touching the slab edge at a corner read as under the slab, headroom −floorThickness.
    if (perimeter <= 0 || (2 * planArea(shared)) / perimeter < overhead.minOverlap) {
        return null;
    }
    const highest: number = Math.max(...shared.map((point: IPlanPoint) => nosingHeight(tread.nosing, point)));
    return overhead.underside - highest;
}

/** The slab around a rectangular opening as four rectangles, underside at `floorHeight`. */
function slabAround(config: IStairConfig): IOverhead[] {
    const far: number = config.openingZ + config.openingLength;
    const right: number = config.openingX + config.openingWidth;
    const rects: [number, number, number, number][] = [
        [0, 0, config.roomWidth, config.openingZ],
        [0, far, config.roomWidth, config.roomLength],
        [0, config.openingZ, config.openingX, far],
        [right, config.openingZ, config.roomWidth, far],
    ];
    return rects
        .filter(([x0, z0, x1, z1]: [number, number, number, number]) => x1 - x0 >= 1 && z1 - z0 >= 1)
        .map(([x0, z0, x1, z1]: [number, number, number, number]) => ({
            underside: config.floorHeight,
            top: config.floorHeight + config.floorThickness,
            minOverlap: MIN_OVERLAP,
            outline: [
                { x: x0, z: z0 },
                { x: x1, z: z0 },
                { x: x1, z: z1 },
                { x: x0, z: z1 },
            ],
        }));
}

/** Every tread with its walking outline (no side overhang) and the rise to the next nose. */
function treadPlans(steps: readonly IStepGeometry[], totalRise: number, placement: IPlanPoint): ITreadPlan[] {
    return steps.map((step: IStepGeometry, order: number) => {
        const nextTop: number = order + 1 < steps.length ? steps[order + 1].position.y : totalRise;
        const rise: number = step.isLanding ? 0 : Math.max(0, nextTop - step.position.y);
        return {
            index: step.index,
            // A kosour tread's 6 mm side lip past the boards is neither walked on nor walked under:
            // the outline stops at the stair edge, so a lip over the next flight's end is not overhead.
            outline: toRoom(treadFootprint(step, 'tetiva'), placement),
            nosing: nosingFrame(step, rise, placement),
        };
    });
}

/**
 * Headroom as DIN 18065 and the IRC measure it: vertically from the nosing line — the line through
 * the front top edges of the treads, nose tips included — up to the underside of whatever is
 * above: the slab around the opening (underside at `floorHeight`) or a higher tread or landing of
 * the same stair. A flat landing is measured from its top. The stair stands where `placement`
 * puts it in the room, the same place the 3D draws it. The next step up is not counted as
 * overhead: its nose overhangs this tread by design. A tread that rises into the slab gets a
 * negative headroom. `withSlab` is false for a spiral: its round opening clears every tread.
 */
export function calculateHeadroom(
    steps: readonly IStepGeometry[],
    config: IStairConfig,
    placement: IPlanPoint,
    withSlab: boolean = true
): IHeadroomResult {
    const ordered: IStepGeometry[] = [...steps].sort((a: IStepGeometry, b: IStepGeometry) => a.index - b.index);
    const plans: ITreadPlan[] = treadPlans(ordered, config.floorHeight + config.floorThickness, placement);
    const slab: IOverhead[] = withSlab ? slabAround(config) : [];
    const treadsOverhead: IOverhead[] = ordered.map((step: IStepGeometry, order: number) => ({
        outline: plans[order].outline,
        top: step.position.y,
        underside: step.position.y - step.treadThickness,
        // At a winder pivot a nose reaches over the corner of the step two below; a strip no
        // wider than a nose is that overhang, not a stair part over the walker's head.
        minOverlap: config.noseLength + MIN_OVERLAP,
    }));
    const stepHeadroom: Map<number, number> = new Map<number, number>();

    plans.forEach((tread: ITreadPlan, order: number) => {
        const higher: IOverhead[] = treadsOverhead.slice(order + 2).filter((overhead: IOverhead) => overhead.top > tread.nosing.top);
        const clearances: number[] = [...slab, ...higher]
            .map((overhead: IOverhead) => clearanceUnder(tread, overhead))
            .filter((clearance: number | null): clearance is number => clearance !== null);
        if (clearances.length > 0) {
            stepHeadroom.set(tread.index, Math.min(...clearances));
        }
    });

    const values: number[] = [...stepHeadroom.values()];
    return { stepHeadroom, headroom: values.length > 0 ? Math.min(...values) : config.floorHeight };
}

/** Step indices whose headroom is below `minimum`: exactly the treads lit red for headroom. */
export function lowHeadroomSteps(result: IHeadroomResult, minimum: number): number[] {
    return [...result.stepHeadroom.entries()]
        .filter(([, clearance]: [number, number]) => clearance < minimum)
        .map(([index]: [number, number]) => index);
}
